/* eslint-disable @typescript-eslint/ban-types */
import { CommonEvent, EventAttributes, eventType, ScreenEvent } from '@atlassiansox/analytics-web-client';
import {
  ANALYTICS_SUBPRODUCT_DEFAULT,
  AnalyticsEvent,
  AnalyticsOptions,
  AtlassianAccountIdProvider,
  GlobalPageProperties,
  RolesProvider,
} from './analytics-client';

export default class StubbedAnalyticsClient {
  private aaid?: string;
  private subproduct?: string;
  private aaidProvider!: AtlassianAccountIdProvider;
  private hasSentSetupWarning!: boolean;
  private isNotRunningJest = process.env.JEST_WORKER_ID === undefined;
  private roles?: string[] = undefined;
  private rolesProvider!: RolesProvider;
  private globalPageProperties!: GlobalPageProperties;

  constructor() {
    this.setSubproduct(ANALYTICS_SUBPRODUCT_DEFAULT);
  }

  sendOperationalEvent = async (event: AnalyticsEvent, callback?: Function) => {
    const enrichedEvent = this.enrichCommonEvent(event);

    if (await this.shouldSendMessage()) {
      this.printEvent(eventType.OPERATIONAL, enrichedEvent);
      if (callback) {
        callback();
      }
    }
  };

  sendScreenEvent = async (event: ScreenEvent, callback?: Function) => {
    const enrichedEvent = this.enrichScreenEvent(event);

    if (await this.shouldSendMessage()) {
      this.printEvent(eventType.SCREEN, enrichedEvent);
      if (callback) {
        callback();
      }
    }
  };

  sendTrackEvent = async (event: AnalyticsEvent, callback?: Function) => {
    const enrichedEvent = this.enrichCommonEvent(event);

    if (await this.shouldSendMessage()) {
      this.printEvent(eventType.TRACK, enrichedEvent);
      if (callback) {
        callback();
      }
    }
  };

  sendUIEvent = async (event: AnalyticsEvent, callback?: Function) => {
    const enrichedEvent = this.enrichCommonEvent(event);

    if (await this.shouldSendMessage()) {
      this.printEvent(eventType.UI, enrichedEvent);
      if (callback) {
        callback();
      }
    }
  };

  setSubproduct = (subproduct: string) => {
    this.subproduct = subproduct;
  };

  setAtlassianAccountId = (aaid: string) => {
    this.aaid = aaid;
  };

  printEvent = (eventType: string, event: CommonEvent | ScreenEvent) => {
    global.console.debug(`Analytics: ${this.subproduct} - ${this.aaid} - ${eventType}: `, event);
  };

  setup = async (aaidProvider: AtlassianAccountIdProvider, options: AnalyticsOptions) => {
    this.aaidProvider = aaidProvider;

    if (options && options.subproduct) {
      this.setSubproduct(options.subproduct);
    }
  };

  setupRoles = async (rolesProvider: RolesProvider) => {
    try {
      this.rolesProvider = rolesProvider;

      if (this.rolesProvider) {
        const roles = await this.rolesProvider();
        this.setRoles(roles);
      }
    } catch (error) {
      this.handleProviderException(error as Error);
    }
  };

  shouldSendMessage = async () => {
    try {
      if (this.aaidProvider) {
        const aaid = await this.aaidProvider();
        this.setAtlassianAccountId(aaid);
        return true;
      }
    } catch (error) {
      this.handleProviderException(error as Error);
    }

    if (!this.hasSentSetupWarning && this.isNotRunningJest) {
      global.console.warn('[DEV NOTE] Please setup analytics in your application before trying to send events');

      this.hasSentSetupWarning = true;
    }
    return false;
  };

  setGlobalPageProperties = (properties: GlobalPageProperties) => {
    this.globalPageProperties = properties;
  };

  startUIViewedEvent = async () => {
    if (await this.shouldSendMessage()) {
      global.console.debug(`Analytics: ${this.subproduct} - ui viewed event created - ${this.aaid} `);
    }
  };

  private enrichCommonEvent = (event: AnalyticsEvent): CommonEvent => {
    const enrichedAttributes = this.enrichAttributes(event.attributes);

    return {
      source: this.globalPageProperties && this.globalPageProperties.source,
      ...event,
      attributes: {
        ...(this.globalPageProperties && this.globalPageProperties.pageAttributes),
        ...enrichedAttributes,
      },
    } as CommonEvent;
  };

  private enrichScreenEvent = (event: ScreenEvent): ScreenEvent => {
    const enrichedAttributes = this.enrichAttributes(event.attributes);

    return {
      ...event,
      attributes: {
        ...enrichedAttributes,
      },
    };
  };

  private enrichAttributes = (attributes?: EventAttributes) => {
    const roles = this.roles ? this.roles : undefined;

    return {
      ...attributes,
      roles,
    };
  };

  private setRoles = (roles: string[]) => {
    this.roles = roles;
  };

  private hasSentProviderException = false;
  private handleProviderException = (error: Error) => {
    if (!this.hasSentProviderException) {
      global.console.warn(`[DEV NOTE] Unable to setup the AAID Provider: ${error}`);

      this.hasSentProviderException = true;
    }
  };
}
